/* eslint-disable @typescript-eslint/no-explicit-any */
import Image from "next/image";
import { useState } from "react";

export const FALLBACK_IMAGES = {
  DEFAULT: "/images/fallback-image.svg",
  NO_VIDEO: "/images/no-video-record.png",
} as const;

type FallbackImageProps = {
  fallbackSrc?: string;
  fallbackType?: keyof typeof FALLBACK_IMAGES;
  src?: string;
  alt: string;
  allowedDomains?: string[];
  [key: string]: any;
};

// Detect if hostname is an S3 domain
const isS3Domain = (hostname: string) => {
  return (
    hostname === "s3.amazonaws.com" ||
    /^s3\.[a-z0-9-]+\.amazonaws\.com$/.test(hostname) ||
    /^.*\.s3\.amazonaws\.com$/.test(hostname) ||
    /^.*\.s3\.[a-z0-9-]+\.amazonaws\.com$/.test(hostname)
  );
};

export default function FallbackImage({
  fallbackSrc,
  fallbackType = "DEFAULT",
  src,
  alt,
  allowedDomains = ["adex.itbee-gitlab.com"],
  ...props
}: FallbackImageProps) {
  const getFallbackSrc = () => fallbackSrc || FALLBACK_IMAGES[fallbackType];

  const isDomainAllowed = (hostname: string) => {
    // 1. Always allow S3 dynamic URLs
    if (isS3Domain(hostname)) return true;

    // 2. Allow manually configured domains
    return allowedDomains.includes(hostname);
  };

  const normalizeSrc = (s?: string) => {
    if (!s) return getFallbackSrc();

    if (s.startsWith("blob:") || s.startsWith("data:")) return s;

    try {
      const url = new URL(s);
      return isDomainAllowed(url.hostname) ? s : getFallbackSrc();
    } catch {
      return getFallbackSrc();
    }
  };

  const [imgSrc, setImgSrc] = useState(normalizeSrc(src));

  return (
    <Image
      {...props}
      src={imgSrc}
      alt={alt}
      onError={() => setImgSrc(getFallbackSrc())}
      unoptimized
    />
  );
}
